import { useState, useEffect, useCallback } from 'react';
import { PLANBASE_CONFIG } from '../config';
import { getUserAuthHeaders, decodeJWT } from '../utils/auth';

interface ProjectInteractions {
  likes: number;
  isLiked: boolean;
  isBookmarked: boolean;
  toggleLike: () => Promise<void>;
  toggleBookmark: (category?: string) => Promise<void>;
  loading: boolean;
}

// Local storage keys
const LIKES_CACHE_KEY = 'planbase_likes_cache';
const USER_LIKES_KEY = 'planbase_user_likes';
const USER_BOOKMARKS_KEY = 'planbase_user_bookmarks';

// Helper to decode JWT and get user ID
function getUserIdFromToken(token: string): string | null {
  try {
    const payload = decodeJWT(token);
    return payload?.sub || payload?.user_id || payload?.id || null;
  } catch {
    return null;
  }
}

// Get cached likes count
function getCachedLikes(pageId: string): number | null {
  try {
    const cache = JSON.parse(localStorage.getItem(LIKES_CACHE_KEY) || '{}');
    return cache[pageId] ?? null;
  } catch {
    return null;
  }
}

// Set cached likes count
function setCachedLikes(pageId: string, count: number): void {
  try {
    const cache = JSON.parse(localStorage.getItem(LIKES_CACHE_KEY) || '{}');
    cache[pageId] = count;
    localStorage.setItem(LIKES_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Silent fail
  }
}

// Check if user has liked a project (local)
function getUserLikedStatus(pageId: string): boolean {
  try {
    const likes = JSON.parse(localStorage.getItem(USER_LIKES_KEY) || '[]');
    return likes.includes(pageId);
  } catch {
    return false;
  }
}

// Set user liked status (local)
function setUserLikedStatus(pageId: string, liked: boolean): void {
  try {
    const likes = JSON.parse(localStorage.getItem(USER_LIKES_KEY) || '[]');
    if (liked && !likes.includes(pageId)) {
      likes.push(pageId);
    } else if (!liked) {
      const index = likes.indexOf(pageId);
      if (index > -1) likes.splice(index, 1);
    }
    localStorage.setItem(USER_LIKES_KEY, JSON.stringify(likes));
  } catch {
    // Silent fail
  }
}

// Check if user has bookmarked a project (local)
function getUserBookmarkedStatus(pageId: string): boolean {
  try {
    const bookmarks = JSON.parse(localStorage.getItem(USER_BOOKMARKS_KEY) || '[]');
    return bookmarks.includes(pageId);
  } catch {
    return false;
  }
}

// Set user bookmarked status (local)
function setUserBookmarkedStatus(pageId: string, bookmarked: boolean): void {
  try {
    const bookmarks = JSON.parse(localStorage.getItem(USER_BOOKMARKS_KEY) || '[]');
    if (bookmarked && !bookmarks.includes(pageId)) {
      bookmarks.push(pageId);
    } else if (!bookmarked) {
      const index = bookmarks.indexOf(pageId);
      if (index > -1) bookmarks.splice(index, 1);
    }
    localStorage.setItem(USER_BOOKMARKS_KEY, JSON.stringify(bookmarks));
  } catch {
    // Silent fail
  }
}


export function useProjectInteractions(projectId: string | number | undefined, defaultCategory: string = 'projects'): ProjectInteractions {
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('planbase_token');
  const userId = token ? getUserIdFromToken(token) : null;
  const pageId = projectId ? `project_${projectId}` : null;

  // Initialize from local cache immediately (optimistic)
  useEffect(() => {
    if (!pageId) return;
    
    const cachedLikes = getCachedLikes(pageId);
    if (cachedLikes !== null) {
      setLikes(cachedLikes);
    }
    
    setIsLiked(getUserLikedStatus(pageId));
    setIsBookmarked(getUserBookmarkedStatus(pageId));
  }, [pageId]);

  // Fetch actual state from server and sync
  useEffect(() => {
    const fetchState = async () => {
      if (!token || !userId || !pageId) return;
      if (!PLANBASE_CONFIG.apiKey) return;

      // Build headers with both API key and user token
      const headers = {
        ...getUserAuthHeaders(),
        'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
        'X-Project-ID': PLANBASE_CONFIG.projectId,
        'X-User-Token': token,
      };

      try {
        const likesResponse = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/likes/${encodeURIComponent(pageId)}`,
          { headers }
        );

        if (likesResponse.ok) {
          const likesData = await likesResponse.json();
          const serverLikes = likesData.likes || 0;
          const serverUserHasLiked = likesData.userHasLiked || false;
          setLikes(serverLikes);
          setCachedLikes(pageId, serverLikes);
          setIsLiked(serverUserHasLiked);
          setUserLikedStatus(pageId, serverUserHasLiked);
        }

        const bookmarksResponse = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/bookmarks`,
          { headers }
        );

        if (bookmarksResponse.ok) {
          const bookmarksData = await bookmarksResponse.json();
          const isProjectBookmarked = bookmarksData.data?.some(
            (bookmark: any) => bookmark.slug === pageId
          ) || false;
          setIsBookmarked(isProjectBookmarked);
          setUserBookmarkedStatus(pageId, isProjectBookmarked);
        }
      } catch {
        // Keep using cached values on error
      }
    };

    fetchState();
  }, [projectId, token, userId, pageId]);


  const toggleLike = useCallback(async () => {
    if (!token || !userId || !pageId || loading) return;

    const previousLikes = likes;
    const previousIsLiked = isLiked;
    
    if (isLiked) {
      setIsLiked(false);
      setLikes(prev => Math.max(0, prev - 1));
      setUserLikedStatus(pageId, false);
      setCachedLikes(pageId, Math.max(0, previousLikes - 1));
    } else {
      setIsLiked(true);
      setLikes(prev => prev + 1);
      setUserLikedStatus(pageId, true);
      setCachedLikes(pageId, previousLikes + 1);
    }

    const headers = {
      ...getUserAuthHeaders(),
      'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
      'X-Project-ID': PLANBASE_CONFIG.projectId,
      'X-User-Token': token,
      'Content-Type': 'application/json',
    };

    setLoading(true);
    try {
      if (previousIsLiked) {
        const response = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/likes/${encodeURIComponent(pageId)}`,
          { method: 'DELETE', headers }
        );
        if (!response.ok) {
          setIsLiked(previousIsLiked); setLikes(previousLikes);
          setUserLikedStatus(pageId, previousIsLiked); setCachedLikes(pageId, previousLikes);
        }
      } else {
        const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/likes`, {
          method: 'POST', headers,
          body: JSON.stringify({ page_id: pageId }),
        });
        if (!response.ok) {
          setIsLiked(previousIsLiked); setLikes(previousLikes);
          setUserLikedStatus(pageId, previousIsLiked); setCachedLikes(pageId, previousLikes);
        }
      }
    } catch {
      setIsLiked(previousIsLiked); setLikes(previousLikes);
      setUserLikedStatus(pageId, previousIsLiked); setCachedLikes(pageId, previousLikes);
    } finally {
      setLoading(false);
    }
  }, [token, userId, pageId, loading, likes, isLiked]);


  const toggleBookmark = useCallback(async (category?: string) => {
    if (!token || !userId || !pageId || loading) return;

    const collectionName = category || defaultCategory;
    
    // Optimistic update
    const previousIsBookmarked = isBookmarked;
    
    if (isBookmarked) {
      setIsBookmarked(false);
      setUserBookmarkedStatus(pageId, false);
    } else {
      setIsBookmarked(true);
      setUserBookmarkedStatus(pageId, true);
    }

    setLoading(true);
    try {
      if (previousIsBookmarked) {
        const bookmarksResponse = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/bookmarks`,
          {
            headers: getUserAuthHeaders(),
          }
        );
        
        if (bookmarksResponse.ok) {
          const bookmarksData = await bookmarksResponse.json();
          
          // The API returns bookmarks in data.data, not data.bookmarks
          const bookmark = bookmarksData.data?.find(
            (b: any) => b.slug === pageId
          );
          
          if (bookmark) {
            const response = await fetch(
              `${PLANBASE_CONFIG.apiUrl}/sdk/bookmarks/${bookmark.id}`,
              {
                method: 'DELETE',
                headers: getUserAuthHeaders(),
              }
            );
            
            if (!response.ok) {
              setIsBookmarked(previousIsBookmarked);
              setUserBookmarkedStatus(pageId, previousIsBookmarked);
            }
          }
        } else {
          setIsBookmarked(previousIsBookmarked);
          setUserBookmarkedStatus(pageId, previousIsBookmarked);
        }
      } else {
        const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/bookmarks`, {
          method: 'POST',
          headers: getUserAuthHeaders(),
          body: JSON.stringify({
            slug: pageId,
            collection_name: collectionName,
          }),
        });
        
        if (!response.ok) {
          setIsBookmarked(previousIsBookmarked);
          setUserBookmarkedStatus(pageId, previousIsBookmarked);
        }
      }
    } catch (error) {
      setIsBookmarked(previousIsBookmarked);
      setUserBookmarkedStatus(pageId, previousIsBookmarked);
    } finally {
      setLoading(false);
    }
  }, [token, userId, pageId, loading, isBookmarked, defaultCategory]);

  return {
    likes,
    isLiked,
    isBookmarked,
    toggleLike,
    toggleBookmark,
    loading,
  };
}
